import { FC, JSX, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import AccordionBtn from 'components/commercetools-ui/atoms/accordion';
import TrustBox from 'components/commercetools-ui/organisms/trustpilot';
import { useFormat } from 'helpers/hooks/useFormat';
import AccordionComponent from 'components/commercetools-ui/atoms/accordion/AccordianComponent';

const Markdown = dynamic(() => import('../../../markdown'));

// Define props for the AdditionalInfo component
type AdditionalInfoProps = {
  productspec?: string;
  description?: string;
  productImage?: string;
  trustPilotSkuId?: string;
  selectedVariant?: string;
  scrollToRefReview?: any;
  reviewOpen?: boolean;
  setReviewOpen?: (value: boolean) => void;
};

interface AccordionRef {
  isOpen: boolean;
}

const junRemove: Record<string, string> = {
  '&#39;': "'",
};

const decodeString = (input: string): string => {
  return Object.keys(junRemove).reduce((decoded, key) => decoded.replaceAll(key, junRemove[key]), input);
};

// Functional component to display additional product information
const AdditionalInfo: FC<AdditionalInfoProps> = ({
  productspec,
  description,
  productImage,
  trustPilotSkuId,
  selectedVariant,
  scrollToRefReview,
  reviewOpen,
  setReviewOpen,
}) => {
  const { formatMessage } = useFormat({ name: 'product' });
  // const isEmptyParagraph = description && /<p>\s*<\/p>/.test(description);
  // Function to render the description with accordions for headings
  const accordionRef = useRef<AccordionRef>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleAccordionToggle = () => {
    const isOpen = accordionRef.current?.isOpen;
    if (!isOpen) {
      setIsExpanded(false); // Reset "Read More" when accordion is closed
    }
  };
  const cleanHtmlString = (html: string): string => {
    // Remove unwanted HTML tags or extra spaces between tags
    return html.replace(/<\s*(\/?[\w]+)\s*([^>]*)\s*\/?>/g, (match, tag, attrs) => {
      return `<${tag}${attrs ? ' ' + attrs.trim() : ''}>`;
    });
  };
  const renderDescriptionWithAccordions = (htmlString: string, productCode?: string) => {
    // Clean the HTML string before processing it
    const cleanedHtmlString = cleanHtmlString(htmlString);

    const headingTagRegex = /<h[34]><strong.*?>.*?<\/strong><\/h[34]>/g;
    const headingMatches = cleanedHtmlString.match(headingTagRegex);

    // If no headings, return the entire description
    if (!headingMatches) {
      return (
        <>
          {productCode && <p className="border-t border-neutral-400 pb-2 pt-4 text-sm">Product Code - {productCode}</p>}
          <div
            className="font-regular font-body text-14 leading-loose text-primary-black"
            dangerouslySetInnerHTML={{ __html: cleanedHtmlString }}
          />
        </>
      );
    }

    const contentSections: JSX.Element[] = [];
    let lastIndex = 0;
    let appliedReadMore = false; // Flag to ensure logic applies only to the first H3
    let isFirstAccordion = true;

    if (productCode) {
      contentSections.push(
        <p key="product-code" className="border-t border-neutral-400 pb-2 pt-4 text-sm">
          Product Code - {productCode}
        </p>,
      );
    }

    headingMatches.forEach((headingMatch: string, index: number) => {
      const headingStartIndex = cleanedHtmlString.indexOf(headingMatch, lastIndex);
      // Get content before this heading
      const beforeHeadingContent = cleanedHtmlString.slice(lastIndex, headingStartIndex);
      if (beforeHeadingContent) {
        contentSections.push(
          <div
            key={`content-${index}`}
            className="font-regular font-body text-14 leading-loose text-primary-black"
            dangerouslySetInnerHTML={{ __html: beforeHeadingContent }}
          />,
        );
      }
      // Determine if the heading is <h3> or <h4>
      const isH3 = headingMatch.startsWith('<h3>');
      const headingContent = decodeString(headingMatch.replace(/<\/?h[34]>|<\/?strong>/g, ''));

      // For <h3>, create an accordion; for <h4>, render as a normal heading
      if (isH3) {
        // Find content after the current <h3> until the next heading or the end
        const nextHeadingStartIndex = cleanedHtmlString.indexOf(headingMatches[index + 1], headingStartIndex);
        const afterHeadingContent =
          nextHeadingStartIndex === -1
            ? cleanedHtmlString.slice(headingStartIndex + headingMatch.length)
            : cleanedHtmlString.slice(headingStartIndex + headingMatch.length, nextHeadingStartIndex);

        if (!appliedReadMore) {
          const listRegex = /<(ul|ol)>(.*?)<\/\1>/s;
          const listMatch = afterHeadingContent.match(listRegex);

          if (listMatch) {
            const listContent = listMatch[0];
            const contentAfterList = afterHeadingContent.slice(listMatch.index! + listContent.length);

            // Split content after the list into words and check if it has more than a certain number of words
            const words = contentAfterList.split(' ');

            // Set a threshold for the number of words for "Read More"
            const wordThreshold = 29;

            if (words.length > wordThreshold) {
              const stripTags = (html: string) => html.replace(/<\/?p>/g, '');

              const initialContent = stripTags(words.slice(0, wordThreshold).join(' '));
              const remainingContent = stripTags(words.slice(wordThreshold).join(' '));

              contentSections.push(
                <AccordionBtn
                  variant="plusAndMinus"
                  key={`accordion-${index}`}
                  className={`items-center py-4 text-start text-14 font-black ${
                    isFirstAccordion ? '' : 'border-t'
                  } border-b border-neutral-400`}
                  closedSectionTitle={headingContent}
                  isHeading={true}
                  buttonClassName="text-start py-10 font-body text-14 font-extrabold leading-tight text-primary-black"
                  panelClassName="pb-2 text-primary-black border-t-2"
                  defaultOpen={index === 0}
                  onClick={handleAccordionToggle}
                >
                  <div className="font-regular additional-info font-body text-14 leading-loose text-primary-black">
                    <div dangerouslySetInnerHTML={{ __html: listContent }} />
                    <p>
                      <span dangerouslySetInnerHTML={{ __html: initialContent }} />
                      {!isExpanded && (
                        <span
                          className="cursor-pointer text-black underline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsExpanded(true);
                          }}
                        >
                          ...Read More
                        </span>
                      )}
                      {isExpanded && (
                        <>
                          <span className="pl-3" dangerouslySetInnerHTML={{ __html: remainingContent }} />
                        </>
                      )}
                    </p>
                  </div>
                </AccordionBtn>,
              );
              isFirstAccordion = false;
              appliedReadMore = true;
              lastIndex = nextHeadingStartIndex === -1 ? cleanedHtmlString.length : nextHeadingStartIndex;
              return;
            }
          }
        }

        // Push accordion with content
        contentSections.push(
          <AccordionBtn
            variant="plusAndMinus"
            key={`accordion-${index}`}
            className="items-center border-b border-neutral-400 py-10 text-start text-14 font-black"
            closedSectionTitle={headingContent}
            isHeading={true}
            buttonClassName="text-start py-10 font-body text-14 font-extrabold leading-tight text-primary-black"
            panelClassName="pb-2 text-primary-black border-t-2"
            defaultOpen={index === 0}
          >
            <div
              className="font-regular font-body text-14 leading-loose text-primary-black"
              dangerouslySetInnerHTML={{ __html: afterHeadingContent }}
            />
          </AccordionBtn>,
        );
        // Update lastIndex to avoid reprocessing
        lastIndex = nextHeadingStartIndex === -1 ? cleanedHtmlString.length : nextHeadingStartIndex;
      } else {
        // Render <h4> as a normal heading
        contentSections.push(
          <h4 key={`heading-${index}`} className="font-body text-16 font-semibold leading-tight text-primary-black">
            {headingContent}
          </h4>,
        );

        // Update lastIndex for next iteration
        lastIndex = headingStartIndex + headingMatch.length;
      }
    });
    // Handle content after the last heading
    const afterLastHeadingContent = cleanedHtmlString.slice(lastIndex);
    if (afterLastHeadingContent) {
      contentSections.push(
        <div
          key="after-last-heading"
          className="font-regular font-body text-14 leading-loose text-primary-black"
          dangerouslySetInnerHTML={{ __html: afterLastHeadingContent }}
        />,
      );
    }

    return contentSections;
  };

  return (
    <div className="additional-info col-span-2 md:mx-40 2xl:mx-auto">
      {/* {!isEmptyParagraph && ( */}
      {/* // )} */}

      <style>
        {`
           .additional-info ul,
           .additional-info ul li {
             margin-left: 14px !important;
             list-style-type: disc !important;
             list-style: disc !important;
             margin-top: 0.5rem;
             margin-bottom: 0.5rem;
           }
           .additional-info ol,
           .additional-info ol li {
             margin-left: 14px !important;
             list-style-type: decimal !important;
             list-style: decimal !important;
             margin-top: 0.5rem;
             margin-bottom: 0.5rem;
          }
          .additional-info li {
              line-height: 1.5;
              font-weight: 400;
              font-size: 14px;
              font-family: URW DIN, sans-serif;
              margin-bottom: 0.5rem;
              list-style-position: outside;
          }
          .additional-info li::marker {
              font-weight: 700;
              font-size: 16px;
              color: rgb(33, 33, 33);
}
          .additional-info strong {
              font-weight: 700;
              font-size: 14px;
              margin-bottom: 10px;
              display: block;
              font-family: URW DIN, sans-serif;
          }
          button div p {
          font-weight : 700
          }

          .additional-info u {
    text-decoration: underline;
          }
          .additional-info strong {
          font-weight: 700;
          font-size: 14px;
          margin-bottom: 10px;
          display: block;
          }
        `}
      </style>

      {/* {description && (
        // <AccordionBtn
        //   variant="plusAndMinus"
        //   className="col-span-2 mt-24 border-b border-neutral-400 text-26 md:mt-0 md:text-30"
        //   buttonClassName="py-21 font-body text-20 leading-tight text-primary-black pl-0 md:pr-12"
        //   panelClassName="pb-20 md:pt-10 md:pb-30 text-primary-black text-20"
        // >
        <div className="mb-25">
          <div className="mx-20 md:mx-40 lg:mx-auto lg:w-[880px] 2xl:w-[73rem]">
            <div className="mb-10 mt-8">
              <h4 className="text-start font-body text-20 font-semibold uppercase leading-6">
                {formatMessage({ id: 'product.details', defaultMessage: 'PRODUCT DETAILS' })}
              </h4>
            </div>
            {renderDescriptionWithAccordions(description)}
          </div>
        </div>
        // </AccordionBtn>
      )} */}
      {description && (
        <div className="mb-25">
          <div className="mx-20 md:mx-40 lg:mx-auto lg:w-[880px] 2xl:w-[73rem]">
            <div className="mb-10 mt-8">
              <h4 className="text-start font-body text-20 font-semibold uppercase leading-6">
                {formatMessage({ id: 'product.details', defaultMessage: 'PRODUCT DETAILS' })}
              </h4>
            </div>
            {renderDescriptionWithAccordions(description)}
            {selectedVariant && (
              <div>
                <p className="mt-4 text-sm">Product Code - {selectedVariant}</p>{' '}
              </div>
            )}
          </div>
        </div>
      )}

      {productspec && (
        <AccordionBtn
          className="col-span-2 border-b border-neutral-400 md:mt-0"
          closedSectionTitle={formatMessage({ id: 'details.additional', defaultMessage: 'Additional Details' })}
          buttonClassName="py-21 font-body text-16 leading-tight text-primary-black pl-0 md:pr-12"
          panelClassName="pb-20 md:pt-10 md:pb-30 text-primary-black"
        >
          <Markdown
            className="font-regular block font-body text-14 leading-loose text-primary-black"
            markdown={productspec}
          />
        </AccordionBtn>
      )}
      <div ref={scrollToRefReview}>
        {trustPilotSkuId && (
          <>
            <div className="mx-20 mb-[20px] md:mx-40 lg:mx-auto lg:w-[880px] 2xl:w-[73rem]">
              <div className="mb-10 mt-8">
                <h4 className="text-start font-body text-20 font-semibold uppercase leading-6">
                  {formatMessage({ id: 'product.reviews ', defaultMessage: 'CUSTOMER REVIEWS' })}
                </h4>
              </div>
              <AccordionComponent
                variant="plusAndMinus"
                className="items-start border-b border-t border-neutral-400 pb-20 pt-5 text-16 font-black"
                closedSectionTitle={'Trustpilot'}
                buttonClassName="py-10 font-body text-14"
                panelClassName="pb-20 md:pt-10 md:pb-30 text-primary-black border-t-2"
                defaultOpen={reviewOpen}
                isOpen={reviewOpen}
                onChange={setReviewOpen}
              >
                <div>
                  <TrustBox id={trustPilotSkuId} showVertical={true} />
                </div>
              </AccordionComponent>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdditionalInfo;
